import React, { useEffect, useRef } from 'react';
import { haptic } from '../../utils/haptics';
import { lineCap } from '../../utils/stockCap';
import styles from './agentChat.module.css';

/**
 * WHAT THE CUSTOMER HAS CHOSEN SO FAR (owner 10-23: "show the selected services so far so they can modify it — same for
 * cart — and for specialist or slot selection"). A compact glass bar in the dock, above the composer: the booking in
 * progress (services · specialist · time) and its running price, a stay (place · dates) and its quote, the cart (the
 * storefront's own — the same lines the drawer holds), and the next step as one button. The booking and the stay are
 * the SERVER's (they live in the conversation, so a customer can talk about something else and come back); the cart is
 * the CartContext's. Nothing shows when all three are empty.
 *
 * Tapping the bar opens a sheet that grows out of it (§6.4) and shrinks back into it (§6.5): each service with ×, the
 * specialist and the time with Change, "How much?", the stay's dates and guests with Change and its request, each cart
 * line with − / + / × and the checkout. A step that asks something new (Change, How much?, the next step) closes the
 * sheet, so the question in the conversation is in view; a removal keeps it open.
 */
const SIGN = { ILS: '₪', USD: '$', EUR: '€', GBP: '£' };
const moneyOf = (n, currency) => `${SIGN[currency] ?? ''}${(Math.round(Number(n || 0) * 100) / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

function bookingLine(b, t) {
  const services = (b.services || []).map((s) => s.name).filter(Boolean).join(' + ');
  const who = b.specialist?.any ? t('agent.anyone', { defaultValue: 'Anyone free' }) : b.specialist?.name || '';
  return [services, who, b.when || b.day || ''].filter(Boolean).join(' · ');
}

function stayLine(st) {
  return [st.property?.name, st.dates].filter(Boolean).join(' · ');
}

function cartCount(cart, t) {
  const n = Number(cart?.count || 0);
  return n === 1 ? t('agent.cartOne', { defaultValue: '1 item' }) : t('agent.cartItems', { defaultValue: '{{count}} items', count: n });
}

/** The next step the bar offers: the booking's, else the stay's, else the cart's checkout. */
function nextOf(selection, cart, checkoutHref, t) {
  const b = selection?.booking;
  if (b?.next?.label && (b.next.action || b.next.url)) return b.next;
  const st = selection?.stay;
  if (st?.next?.label && (st.next.action || st.next.url)) return st.next;
  if (cart?.count > 0) return { label: t('agent.checkout', { defaultValue: 'Checkout' }), url: checkoutHref };
  return null;
}

export default function SelectionBar({ selection, cart, currency = 'ILS', busy = false, onAct, onOpenUrl, checkoutHref, sheet = null, onSheet, t }) {
  const barRef = useRef(null);
  const sheetRef = useRef(null);
  const booking = selection?.booking && (selection.booking.services || []).length ? selection.booking : null;
  const stay = selection?.stay?.property ? selection.stay : null;
  const lines = cart?.items || [];
  const hasCart = Number(cart?.count || 0) > 0;
  const empty = !booking && !stay && !hasCart;
  const open = sheet === 'open';

  // Opened: the sheet's first control has the keyboard; gone: back to the bar it came out of (§6.9).
  const before = useRef(null);
  useEffect(() => {
    if (sheet === 'open' && before.current !== 'open') sheetRef.current?.querySelector('button, a[href]')?.focus({ preventScroll: true });
    if (sheet === null && before.current) barRef.current?.focus({ preventScroll: true });
    before.current = sheet;
  }, [sheet]);
  // Nothing left to show: the sheet goes too.
  useEffect(() => { if (empty && sheet) onSheet?.(null); }, [empty, sheet, onSheet]);

  if (empty) return null;
  const next = nextOf(selection, cart, checkoutHref, t);
  const go = (step, { keep = false } = {}) => {
    if (!step) return;
    if (!keep) onSheet?.(sheet ? 'closing' : null);
    if (step.url) onOpenUrl?.(step.url);
    else onAct?.(step.label, step.action);
  };
  const toggle = () => {
    if (open) { onSheet?.('closing'); return; }
    haptic('light');
    onSheet?.('open');
  };
  const price = booking?.total || stay?.total || (hasCart && !booking && !stay ? moneyOf(cart.total, currency) : null);

  return (
    <div className={styles.selectionWrap}>
      {sheet ? (
        <div ref={sheetRef} className={`${styles.selectionSheet} ${sheet === 'closing' ? styles.selectionSheetClosing : ''}`} role="region" aria-label={t('agent.selection', { defaultValue: 'Your selection' })} data-testid="agent-selection-sheet">
          <div className={styles.sheetHead}>
            <span className={styles.sheetTitle}>{t('agent.selection', { defaultValue: 'Your selection' })}</span>
            <button type="button" className={styles.sheetClose} onClick={() => onSheet?.('closing')} aria-label={t('agent.selectionClose', { defaultValue: 'Close your selection' })}>×</button>
          </div>

          {booking ? (
            <section className={styles.sheetSection} data-testid="agent-sheet-booking">
              <h3 className={styles.sheetSectionTitle}>{t('agent.selBooking', { defaultValue: 'Booking' })}</h3>
              <ul className={styles.sheetList}>
                {booking.services.map((s) => (
                  <li key={s.id} className={styles.sheetRow}>
                    <span className={styles.sheetRowMain}>{s.name}</span>
                    {s.price ? <span className={styles.sheetRowSide}>{s.price}</span> : null}
                    <button type="button" className={styles.sheetX} disabled={busy} onClick={() => go({ label: t('agent.removeService', { defaultValue: 'Remove {{name}}', name: s.name }), action: { type: 'remove_service', serviceId: s.id } }, { keep: true })} aria-label={t('agent.removeService', { defaultValue: 'Remove {{name}}', name: s.name })}>×</button>
                  </li>
                ))}
                <li className={styles.sheetRow}>
                  <span className={styles.sheetRowLabel}>{t('agent.specialistLabel', { defaultValue: 'Specialist' })}</span>
                  <span className={styles.sheetRowMain}>{booking.specialist?.any ? t('agent.anyone', { defaultValue: 'Anyone free' }) : booking.specialist?.name || t('agent.notChosen', { defaultValue: 'Not chosen yet' })}</span>
                  <button type="button" className={styles.sheetChange} disabled={busy} onClick={() => go({ label: t('agent.changeSpecialist', { defaultValue: 'Change the specialist' }), action: { type: 'change', what: 'specialist' } })} aria-label={t('agent.changeSpecialist', { defaultValue: 'Change the specialist' })}>{t('agent.change', { defaultValue: 'Change' })}</button>
                </li>
                <li className={styles.sheetRow}>
                  <span className={styles.sheetRowLabel}>{t('agent.timeLabel', { defaultValue: 'Time' })}</span>
                  <span className={styles.sheetRowMain}>{booking.when || booking.day || t('agent.notChosen', { defaultValue: 'Not chosen yet' })}</span>
                  <button type="button" className={styles.sheetChange} disabled={busy} onClick={() => go({ label: t('agent.changeTime', { defaultValue: 'Change the time' }), action: { type: 'change', what: 'time' } })} aria-label={t('agent.changeTime', { defaultValue: 'Change the time' })}>{t('agent.change', { defaultValue: 'Change' })}</button>
                </li>
                <li className={styles.sheetRow}>
                  <span className={styles.sheetRowLabel}>{t('agent.total', { defaultValue: 'Total' })}</span>
                  <span className={styles.sheetRowMain}><strong>{booking.total || '—'}</strong>{booking.campaign ? <span className={styles.sheetNote}> · {booking.campaign}</span> : null}</span>
                  <button type="button" className={styles.sheetChange} disabled={busy} onClick={() => go({ label: t('agent.howMuch', { defaultValue: 'How much?' }), action: { type: 'booking_price' } })}>{t('agent.howMuch', { defaultValue: 'How much?' })}</button>
                </li>
              </ul>
              <div className={styles.sheetActions}>
                {booking.next?.label && booking.next.action ? <button type="button" className={styles.primaryButton} disabled={busy} onClick={() => go(booking.next)}>{booking.next.label}</button> : null}
                <button type="button" className={styles.linkButton} disabled={busy} onClick={() => go({ label: t('agent.clearBooking', { defaultValue: 'Clear the booking' }), action: { type: 'booking_clear' } }, { keep: true })}>{t('agent.clearBooking', { defaultValue: 'Clear the booking' })}</button>
              </div>
            </section>
          ) : null}

          {stay ? (
            <section className={styles.sheetSection} data-testid="agent-sheet-stay">
              <h3 className={styles.sheetSectionTitle}>{t('agent.selStay', { defaultValue: 'Stay' })} · {stay.property.name}</h3>
              <ul className={styles.sheetList}>
                <li className={styles.sheetRow}>
                  <span className={styles.sheetRowLabel}>{t('agent.datesLabel', { defaultValue: 'Dates' })}</span>
                  <span className={styles.sheetRowMain}>{stay.dates || t('agent.notChosen', { defaultValue: 'Not chosen yet' })}</span>
                  <button type="button" className={styles.sheetChange} disabled={busy} onClick={() => go({ label: t('agent.changeDates', { defaultValue: 'Change the dates' }), action: { type: 'stay_change', what: 'dates' } })} aria-label={t('agent.changeDates', { defaultValue: 'Change the dates' })}>{t('agent.change', { defaultValue: 'Change' })}</button>
                </li>
                <li className={styles.sheetRow}>
                  <span className={styles.sheetRowLabel}>{t('agent.guestsLabel', { defaultValue: 'Guests' })}</span>
                  <span className={styles.sheetRowMain}>{stay.guests ? t('agent.guests', { defaultValue: '{{count}} guests', count: stay.guests }) : t('agent.notChosen', { defaultValue: 'Not chosen yet' })}</span>
                  <button type="button" className={styles.sheetChange} disabled={busy} onClick={() => go({ label: t('agent.changeGuests', { defaultValue: 'Change the guests' }), action: { type: 'stay_change', what: 'guests' } })} aria-label={t('agent.changeGuests', { defaultValue: 'Change the guests' })}>{t('agent.change', { defaultValue: 'Change' })}</button>
                </li>
                <li className={styles.sheetRow}>
                  <span className={styles.sheetRowLabel}>{t('agent.total', { defaultValue: 'Total' })}</span>
                  <span className={styles.sheetRowMain}><strong>{stay.total || '—'}</strong>{stay.deposit ? <span className={styles.sheetNote}> · {t('agent.deposit', { defaultValue: 'Deposit {{amount}}', amount: stay.deposit })}</span> : null}</span>
                </li>
              </ul>
              <div className={styles.sheetActions}>
                {stay.next?.label && (stay.next.action || stay.next.url) ? <button type="button" className={styles.primaryButton} disabled={busy} onClick={() => go(stay.next)}>{stay.next.label}</button> : null}
                <button type="button" className={styles.linkButton} disabled={busy} onClick={() => go({ label: t('agent.clearStay', { defaultValue: 'Clear the stay' }), action: { type: 'stay_clear' } }, { keep: true })}>{t('agent.clearStay', { defaultValue: 'Clear the stay' })}</button>
              </div>
            </section>
          ) : null}

          {hasCart ? (
            <section className={styles.sheetSection} data-testid="agent-sheet-cart">
              <h3 className={styles.sheetSectionTitle}>{t('agent.selCart', { defaultValue: 'Cart' })}</h3>
              <ul className={styles.sheetList}>
                {lines.map((l) => {
                  const name = l.snapshot?.name || '';
                  const cap = lineCap(l);
                  const atCap = cap !== null && l.quantity >= cap;
                  return (
                    <li key={l.variantId} className={styles.sheetRow} data-testid="agent-cart-line">
                      <span className={styles.sheetRowMain}>{name}</span>
                      <span className={styles.stepper}>
                        <button type="button" className={styles.stepperButton} disabled={busy || l.quantity <= 1} onClick={() => { haptic('selection'); cart.updateQty?.(l.variantId, l.quantity - 1); }} aria-label={t('agent.qtyLess', { defaultValue: 'One less {{name}}', name })}>−</button>
                        <span className={styles.stepperValue} aria-live="polite">{l.quantity}</span>
                        <button type="button" className={styles.stepperButton} disabled={busy || atCap} onClick={() => { haptic('selection'); cart.updateQty?.(l.variantId, l.quantity + 1); }} aria-label={t('agent.qtyMore', { defaultValue: 'One more {{name}}', name })}>+</button>
                      </span>
                      <button type="button" className={styles.sheetX} disabled={busy} onClick={() => { haptic('selection'); cart.removeItem?.(l.variantId); }} aria-label={t('agent.removeLine', { defaultValue: 'Remove {{name}} from the cart', name })}>×</button>
                    </li>
                  );
                })}
                <li className={styles.sheetRow}>
                  <span className={styles.sheetRowLabel}>{t('agent.total', { defaultValue: 'Total' })}</span>
                  <span className={styles.sheetRowMain}><strong>{moneyOf(cart.total, currency)}</strong></span>
                </li>
              </ul>
              <div className={styles.sheetActions}>
                <button type="button" className={styles.primaryButton} disabled={busy} onClick={() => go({ label: t('agent.checkout', { defaultValue: 'Checkout' }), url: checkoutHref })}>{t('agent.checkout', { defaultValue: 'Checkout' })}</button>
              </div>
            </section>
          ) : null}
        </div>
      ) : null}

      <div className={styles.selectionBar} data-testid="agent-selection-bar">
        <button ref={barRef} type="button" className={styles.selectionSummary} onClick={toggle} aria-expanded={open ? 'true' : 'false'} aria-label={open ? t('agent.selectionClose', { defaultValue: 'Close your selection' }) : t('agent.selectionOpen', { defaultValue: 'Show your selection' })}>
          {/* The label sits above what is chosen, so the line has the bar's width on a phone (rig 10-23: "Sw…" at 390 px). */}
          <span className={styles.selectionText}>
            <span className={styles.selectionLabel}>{t('agent.selection', { defaultValue: 'Your selection' })}</span>
            <span className={styles.selectionLines}>
              {booking ? <span className={styles.selectionLine} data-testid="agent-selection-booking">{bookingLine(booking, t)}</span> : null}
              {stay ? <span className={styles.selectionLine} data-testid="agent-selection-stay">{stayLine(stay)}</span> : null}
              {hasCart ? <span className={styles.selectionLine} data-testid="agent-selection-cart">{t('agent.selCart', { defaultValue: 'Cart' })} · {cartCount(cart, t)}</span> : null}
            </span>
          </span>
          {price ? <strong className={styles.selectionPrice}>{price}</strong> : null}
          <svg className={styles.selectionChevron} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"><path d="M6 15l6-6 6 6" /></svg>
        </button>
        {next ? (
          <button type="button" className={styles.selectionNext} disabled={busy} onClick={() => go(next)} data-testid="agent-selection-next">{next.label}</button>
        ) : null}
      </div>
    </div>
  );
}
